import { useState, useEffect } from "react";
import { Cloud, Loader2 } from "lucide-react";
import { Card, CardContent } from "../components/ui/Card";
import { fetchClient } from "../lib/api-client";
import type { paths } from "../lib/api-types";

type WeatherData =
  paths["/api/public/weather"]["get"]["responses"][200]["content"]["application/json"];

function formatValue(v: number | string): string {
  return typeof v === "number" ? String(v) : v;
}

export function Weather() {
  const [data, setData] = useState<WeatherData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchClient
      .GET("/api/public/weather")
      .then(({ data: resData, response }) => {
        if (!response.ok) {
          throw new Error("Weather data temporarily unavailable.");
        }
        setData(resData ?? null);
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="space-y-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Weather station</h1>
        </div>
        <div className="flex items-center justify-center py-12 text-gray-600">
          <Loader2 className="w-8 h-8 animate-spin mr-2" />
          Loading weather…
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Weather station</h1>
        </div>
        <Card>
          <CardContent className="py-12 text-center text-gray-600">
            {error}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Weather station</h1>
        <p className="text-gray-600 mt-1">
          Last observation: {data.observation_time}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <Card variant="elevated">
          <CardContent className="p-5">
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
              <Cloud className="w-4 h-4" />
              Current
            </h2>
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between">
                <dt className="text-gray-600">Temp</dt>
                <dd className="font-medium">{formatValue(data.temp.current)} °C</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-600">Wind</dt>
                <dd className="font-medium">
                  {data.wind.speed} kt {data.wind.direction}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-600">Humidity</dt>
                <dd className="font-medium">{formatValue(data.humidity.current)}%</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-600">Barometer</dt>
                <dd className="font-medium">
                  {formatValue(data.barometer.current)} mb ({data.barometer.trend})
                </dd>
              </div>
            </dl>
          </CardContent>
        </Card>

        <Card variant="elevated">
          <CardContent className="p-5">
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
              Wind
            </h2>
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between">
                <dt className="text-gray-600">10 min avg</dt>
                <dd className="font-medium">{data.wind.ten_min_avg} kt</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-600">10 min gust</dt>
                <dd className="font-medium">{data.wind.ten_min_gust} kt</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-600">Day high</dt>
                <dd className="font-medium">
                  {data.wind.day_high} kt ({data.wind.day_high_time})
                </dd>
              </div>
            </dl>
          </CardContent>
        </Card>

        <Card variant="elevated">
          <CardContent className="p-5">
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
              Rain (mm)
            </h2>
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between">
                <dt className="text-gray-600">Today</dt>
                <dd className="font-medium">{data.rain.day}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-600">Month</dt>
                <dd className="font-medium">{data.rain.month}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-600">Year</dt>
                <dd className="font-medium">{data.rain.year}</dd>
              </div>
            </dl>
          </CardContent>
        </Card>

        <Card variant="elevated">
          <CardContent className="p-5">
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
              Temperature
            </h2>
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between">
                <dt className="text-gray-600">Day low</dt>
                <dd className="font-medium">
                  {data.temp.day_low} °C ({data.temp.day_low_time})
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-600">Day high</dt>
                <dd className="font-medium">
                  {data.temp.day_high} °C ({data.temp.day_high_time})
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-600">Month</dt>
                <dd className="font-medium">
                  {data.temp.month_low} / {data.temp.month_high} °C
                </dd>
              </div>
            </dl>
          </CardContent>
        </Card>

        <Card variant="elevated">
          <CardContent className="p-5">
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
              Windchill / Heat index
            </h2>
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between">
                <dt className="text-gray-600">Windchill day low</dt>
                <dd className="font-medium">
                  {data.windchill.day_low} °C ({data.windchill.day_low_time})
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-600">Heat index day high</dt>
                <dd className="font-medium">
                  {data.heatIndex.day_high} °C ({data.heatIndex.day_high_time})
                </dd>
              </div>
            </dl>
          </CardContent>
        </Card>

        <Card variant="elevated">
          <CardContent className="p-5">
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
              Sunrise / sunset
            </h2>
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between">
                <dt className="text-gray-600">Sunrise</dt>
                <dd className="font-medium">{data.astronomy.sunrise}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-600">Sunset</dt>
                <dd className="font-medium">{data.astronomy.sunset}</dd>
              </div>
            </dl>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
